import { PoseLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import type { PoseLandmarks, Landmark } from '../../types/pose';

export class PoseEngine {
  private landmarker: PoseLandmarker | null = null;
  private lastTimestamp: number = -1;

  async initialize(): Promise<void> {
    const vision = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm'
    );
    this.landmarker = await PoseLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath:
          'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      numPoses: 1,
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });
  }

  detectPose(videoElement: HTMLVideoElement, timestampMs: number): PoseLandmarks | null {
    if (!this.landmarker) return null;
    if (timestampMs <= this.lastTimestamp) return null;
    if (videoElement.readyState < 2) return null;
    if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) return null;
    this.lastTimestamp = timestampMs;

    try {
      const results = this.landmarker.detectForVideo(videoElement, timestampMs);
      if (!results.landmarks || results.landmarks.length === 0) return null;

      const rawLandmarks = results.landmarks[0];
      const landmarks: Landmark[] = rawLandmarks.map((lm) => ({
        x: lm.x,
        y: lm.y,
        z: lm.z ?? 0,
        visibility: lm.visibility ?? 0,
      }));

      const avgVisibility =
        landmarks.reduce((sum, lm) => sum + lm.visibility, 0) / landmarks.length;

      return { landmarks, timestamp: timestampMs, confidence: avgVisibility };
    } catch (e) {
      console.warn('[PoseEngine] detection error:', e);
      return null;
    }
  }

  dispose(): void {
    this.landmarker?.close();
    this.landmarker = null;
  }
}
